import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { api, formatApiErrorDetail } from '../utils/api';
import { Plus, Save, X, Edit, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const Zones = () => {
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '', sort_order: 0 });

  useEffect(() => { loadZones(); }, []);

  const loadZones = async () => {
    try {
      const { data } = await api.get('/zones');
      setZones(data);
    } catch (error) {
      toast.error('Echec du chargement des zones');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/zones/${editingId}`, formData);
        toast.success('Zone modifiee');
      } else {
        await api.post('/zones', formData);
        toast.success('Zone creee');
      }
      resetForm();
      loadZones();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  const handleEdit = (zone) => {
    setFormData({ name: zone.name, description: zone.description || '', sort_order: zone.sort_order || 0 });
    setEditingId(zone.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette zone ?')) return;
    try {
      await api.delete(`/zones/${id}`);
      toast.success('Zone supprimee');
      loadZones();
    } catch (error) {
      toast.error('Echec de la suppression');
    }
  };

  const resetForm = () => {
    setFormData({ name: '', description: '', sort_order: 0 });
    setEditingId(null);
    setShowForm(false);
  };

  if (loading) return <Layout><div className="text-center py-12">Chargement...</div></Layout>;

  return (
    <Layout>
      <div data-testid="zones-page">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold" style={{ color: '#0E2D43', fontFamily: 'Work Sans, sans-serif' }}>
            Zones
          </h1>
          {!showForm && (
            <button onClick={() => setShowForm(true)} data-testid="add-zone-button"
              className="flex items-center space-x-2 px-4 py-2 rounded font-medium"
              style={{ backgroundColor: '#0E2D43', color: '#FFFFFF' }}>
              <Plus size={20} /><span>Ajouter Zone</span>
            </button>
          )}
        </div>

        {showForm && (
          <div className="bg-white rounded-lg shadow p-6 border mb-8" style={{ borderColor: '#E5E7EB' }}>
            <h2 className="text-xl font-bold mb-4" style={{ color: '#0E2D43' }}>
              {editingId ? 'Modifier Zone' : 'Nouvelle Zone'}
            </h2>
            <form onSubmit={handleSubmit} data-testid="zone-form">
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#0E2D43' }}>Nom *</label>
                  <input type="text" value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    data-testid="zone-name-input"
                    className="w-full px-4 py-2 border rounded" style={{ borderColor: '#D1D5DB' }}
                    placeholder="ex: Poste chaud, Plonge, Stockage" required />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#0E2D43' }}>Description</label>
                  <input type="text" value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    data-testid="zone-description-input"
                    className="w-full px-4 py-2 border rounded" style={{ borderColor: '#D1D5DB' }}
                    placeholder="Description optionnelle" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#0E2D43' }}>Ordre</label>
                  <input type="number" value={formData.sort_order}
                    onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
                    className="w-full px-4 py-2 border rounded" style={{ borderColor: '#D1D5DB' }} />
                </div>
              </div>
              <div className="flex space-x-3">
                <button type="submit" data-testid="save-zone-button"
                  className="flex items-center space-x-2 px-4 py-2 rounded font-medium"
                  style={{ backgroundColor: '#0E2D43', color: '#FFFFFF' }}>
                  <Save size={18} /><span>Enregistrer</span>
                </button>
                <button type="button" onClick={resetForm}
                  className="flex items-center space-x-2 px-4 py-2 rounded border"
                  style={{ borderColor: '#D1D5DB', color: '#0E2D43' }}>
                  <X size={18} /><span>Annuler</span>
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="bg-white rounded-lg shadow overflow-hidden border" style={{ borderColor: '#E5E7EB' }}>
          <table className="w-full">
            <thead style={{ backgroundColor: '#0E2D43', color: '#FFFFFF' }}>
              <tr>
                <th className="px-6 py-3 text-left text-sm font-medium">Nom</th>
                <th className="px-6 py-3 text-left text-sm font-medium">Description</th>
                <th className="px-6 py-3 text-left text-sm font-medium">Ordre</th>
                <th className="px-6 py-3 text-right text-sm font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {zones.length === 0 ? (
                <tr><td colSpan="4" className="px-6 py-8 text-center" style={{ color: '#6B7280' }}>
                  Aucune zone. Ajoutez votre premiere zone !
                </td></tr>
              ) : zones.map((zone) => (
                <tr key={zone.id} data-testid={`zone-row-${zone.id}`} className="border-t" style={{ borderColor: '#E5E7EB' }}>
                  <td className="px-6 py-4 font-medium" style={{ color: '#0E2D43' }}>{zone.name}</td>
                  <td className="px-6 py-4" style={{ color: '#6B7280' }}>{zone.description || '-'}</td>
                  <td className="px-6 py-4" style={{ color: '#6B7280' }}>{zone.sort_order}</td>
                  <td className="px-6 py-4 text-right">
                    <button onClick={() => handleEdit(zone)} data-testid={`edit-zone-${zone.id}`}
                      className="inline-flex items-center px-3 py-1 rounded mr-2"
                      style={{ backgroundColor: '#F3F4F6', color: '#0E2D43' }}><Edit size={16} /></button>
                    <button onClick={() => handleDelete(zone.id)} data-testid={`delete-zone-${zone.id}`}
                      className="inline-flex items-center px-3 py-1 rounded"
                      style={{ backgroundColor: '#FEE2E2', color: '#991B1B' }}><Trash2 size={16} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
};

export default Zones;
